


























































































































































import { useModalToggle } from '~/compositions/modal-toggle'
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  ref,
  toRefs
} from '~/utils/nuxt3-migration'
import { toCamelCase } from '~/utils/object'
import { useLogger } from '~/compositions/logger'
import ClassifiedOffersService from '~/services/classified/ClassifiedOffersService'
import { useDep } from '~/compositions/dependency-container'
import { useI18n } from '~/compositions/i18n'
import { useSnackbar } from '~/compositions/snackbar'
import { ClassifiedOfferEntry, OfferState } from '~/models/classified/offers'
import { Pagination } from '~/models/search/types'
import CPagination from '~/components/shared/configurable/pagination/CPagination.vue'
import ClassifiedOffer from '~/components/car/offers/ClassifiedOffer.vue'
import { useNamespacedStore } from '~/compositions/store'
import {
  CLASSIFIED_VIEW_NS,
  ClassifiedViewState
} from '~/store/modules/shared/classifieds/view/state'
import TradesService from '~/services/trades/Trades'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'
import CMiniMessageThreadEntry from '~/components/shared/configurable/message/CMiniMessageThreadEntry.vue'
import {
  REMOVE_CLASSIFIED_OFFER,
  SET_CLASSIFIED_OFFERS,
  SET_HAS_OFFERS_FOR_CLASSIFIED,
  SET_OFFER_THREAD_READ,
  UPDATE_CLASSIFIED_OFFER
} from '~/store/modules/shared/classifieds/view/mutation-types'
import { formatMessageThread } from '~/utils/messages'
import MakeOfferModal from '~/components/shared/classified/offers/MakeOfferModal.vue'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { ciAngleLeftLight } from '~/icons/source/solid/angle-left-light'
import { ciOffers } from '~/icons/source/regular/offers'
import { useRequestError } from '~/compositions/request-error'

export default defineComponent({
  components: {
    MakeOfferModal,
    CMiniMessageThreadEntry,
    ClassifiedOffer,
    CPagination
  },

  props: {
    classifiedId: { type: Number, required: true },
    loadedOffer: {
      type: Object as PropType<ClassifiedOfferEntry | null>,
      required: false,
      default: null
    }
  },
  setup(props, { emit }) {
    const modalToggleFunc = useModalToggle()
    const { classifiedId, loadedOffer } = toRefs(props)
    const loading = ref(false)
    const backButtonClicked = ref(false)
    const { commit, state } = useNamespacedStore(CLASSIFIED_VIEW_NS)
    const classifiedOffers = computed(() => {
      return state.classifiedOffers
    })
    const hasOfferForClassified = computed(() => {
      return state.hasOfferForClassified
    })
    const selectedOffer = ref<ClassifiedOfferEntry | null>(loadedOffer.value)
    const pagination = ref<Pagination | null>(null)
    const logger = useLogger()
    const thread = ref(null)
    const snackbar = useSnackbar()
    const classifiedOffersService = useDep(ClassifiedOffersService)
    const { getters: userGetters } = useNamespacedStore<UserState>(USER_NS)
    const { t } = useI18n()
    const currentPage = ref(1)
    const mainHash = computed(() => {
      if (loadedOffer.value?.id) {
        return `offer-${loadedOffer.value.id}`
      }
      return 'offers'
    })
    const transitioning = ref(false)
    const { dispatch } = useNamespacedStore<ClassifiedViewState>(
      CLASSIFIED_VIEW_NS
    )
    const handleRequestError = useRequestError()

    const showMakeOfferModal = ref(false)
    const offerToCounter = ref<ClassifiedOfferEntry | null>(null)

    const numberOfPages = computed(() => {
      if (pagination.value?.total) {
        return Math.ceil(pagination.value.total / pagination.value.perPage)
      }

      return 0
    })

    const ownedDealers = computed(() => {
      if (userGetters('userDealerIds').length) {
        return userGetters('userDealerIds').map((i: any) => {
          return { id: i }
        })
      }
      return []
    })

    const offerCount = computed(() => {
      return state.offerCount
    })
    const swipeDirection = ref('swipe-left')

    // reset offers at start
    if (!loadedOffer.value) {
      commit(SET_CLASSIFIED_OFFERS, [])
    }

    const onDeleteOffer = async (offer: ClassifiedOfferEntry) => {
      try {
        loading.value = true
        await classifiedOffersService.deleteOffer(offer.id)
        commit(REMOVE_CLASSIFIED_OFFER, offer.id)
        await nextTick()
        if (classifiedOffers.value.length === 0) {
          // we just deleted the last one, so refrech?
          handlePageChange(1)
        }
      } catch (error) {
        handleRequestError(error)
      } finally {
        loading.value = false
      }
    }
    const fetchOffersForClassified = async () => {
      try {
        loading.value = true
        const {
          classifiedOffersList
        } = await classifiedOffersService.getOffersForClassified(
          classifiedId.value,
          currentPage.value
        )
        commit(SET_CLASSIFIED_OFFERS, classifiedOffersList.rows)
        pagination.value = classifiedOffersList.pagination
      } catch (error) {
        let errorMsg = t('an error occurred please try again later')
        if (
          error.response?.data?.data?.errors ||
          error.response?.data?.data?.error
        ) {
          errorMsg = toCamelCase(
            error.response?.data?.data?.errors ||
              error.response?.data?.data?.error
          )
        }
        snackbar.error(errorMsg)

        logger.captureError(error)
      } finally {
        loading.value = false
      }
    }

    const handlePageChange = (pg: number) => {
      currentPage.value = pg
      fetchOffersForClassified()
    }

    const onOfferClick = async (offer: ClassifiedOfferEntry) => {
      swipeDirection.value = 'swipe-left'
      selectedOffer.value = offer
      modalToggleFunc.goToNextHash(`offer-${offer.id}`)
      thread.value = null
      await fetchThread()
    }
    const goBack = () => {
      swipeDirection.value = 'swipe-right'
      thread.value = null
      selectedOffer.value = null

      backButtonClicked.value = true
      window.history.go(-1)
    }

    const onModalHashChange = (e: HashChangeEvent) => {
      if (process.client && window.location.hash) {
        if (backButtonClicked.value) {
          e.preventDefault()
          e.stopPropagation()
          backButtonClicked.value = false
          return
        }
        if (window.location.hash === '#' + mainHash.value) {
          swipeDirection.value = 'swipe-right'
          thread.value = null
          selectedOffer.value = null
        } else {
          const splitLocation = window.location.hash.split('-')
          if (splitLocation.length) {
            const offerID = splitLocation[splitLocation.length - 1]
            swipeDirection.value = 'swipe-left'
            selectedOffer.value = classifiedOffers.value.find(
              o => o.id === parseInt(offerID)
            ) as ClassifiedOfferEntry
            thread.value = null
            fetchThread()
          }
        }
      }
    }
    onMounted(async () => {
      modalToggleFunc.setModalInSearch(false)
      modalToggleFunc.setOnHashChangeCallback(onModalHashChange)
      if (loadedOffer.value?.thread?.id) {
        fetchThread()
      } else if (loadedOffer.value?.id) {
        // we have a loaded offer that we only know the id so load all offers
        await fetchOffersForClassified()

        const foundOffer = classifiedOffers.value.find(
          o => o.id === parseInt(loadedOffer.value.id)
        ) as ClassifiedOfferEntry
        if (foundOffer) {
          selectedOffer.value = foundOffer
          swipeDirection.value = 'swipe-left'
          thread.value = null
          fetchThread()
        }
      } else {
        fetchOffersForClassified()
      }
    })

    const hideModal = () => {
      emit('close')
    }

    // following message functions must be moved in-component when messages refactor happens
    // (duplicate from trades)

    const tradesService = useDep(TradesService)
    const userStore = useNamespacedStore<UserState>(USER_NS)

    const fetchThread = async () => {
      if (selectedOffer.value) {
        loading.value = true
        try {
          const result = await tradesService.fetchThread(
            selectedOffer.value.thread.id
          )

          thread.value = formatMessageThread(result)
        } catch (error) {
          logger.captureError(error)
        } finally {
          loading.value = false
        }
      }
    }

    const threadRead = async () => {
      if (selectedOffer.value) {
        try {
          const messageIds = await tradesService.readThread(
            selectedOffer.value.thread.id
          )
          if (messageIds.length) {
            if (thread?.value) {
              thread.value.read = true
            }
            commit(SET_OFFER_THREAD_READ, {
              threadId: selectedOffer.value.thread.id.toString(),
              read: true
            })
            await nextTick()
            userStore.dispatch(`loadUser`)
          }
        } catch (error) {
          logger.captureError(error)
        }
      }
    }

    const onNewMessages = async () => {
      if (selectedOffer.value) {
        try {
          if (thread?.value) {
            thread.value.read = false
          }
          commit(SET_OFFER_THREAD_READ, {
            threadId: selectedOffer.value.thread.id.toString(),
            read: false
          })
          await nextTick()
          userStore.dispatch(`loadUser`)
        } catch (error) {
          logger.captureError(error)
        }
      }
    }

    const updateThread = (newThreadInfo: any) => {
      if (!newThreadInfo || !selectedOffer.value || !thread.value) {
        return
      }
      if (newThreadInfo.clsfd_title) {
        thread.value.clsfd_title = newThreadInfo.clsfd_title
      }

      if (newThreadInfo.participant) {
        thread.value.participant = newThreadInfo.participant
      }

      if (newThreadInfo.name) {
        thread.value.name = newThreadInfo.name
      }

      if (newThreadInfo.read) {
        thread.value.read = newThreadInfo.read
      }

      if (newThreadInfo.owner) {
        thread.value.owner = newThreadInfo.owner
      }

      if (newThreadInfo.clsfd_id) {
        thread.value.clsfd_id = newThreadInfo.clsfd_id
      }

      if (newThreadInfo.last_message_ts) {
        thread.value.last_message_ts = newThreadInfo.last_message_ts
      }
    }
    const deleteThread = () => {}

    const setOfferState = async ({
      offerId,
      state
    }: {
      offerId: number
      state: OfferState
    }) => {
      const successCallback = async (offer: ClassifiedOfferEntry) => {
        if (state === OfferState.WITHDRAWN) {
          commit(REMOVE_CLASSIFIED_OFFER, offer.id)
          commit(SET_HAS_OFFERS_FOR_CLASSIFIED, false)
        } else {
          commit(UPDATE_CLASSIFIED_OFFER, offer)
        }
        await dispatch(`fetchOfferCount`, Boolean(loadedOffer.value))
      }
      loading.value = true
      await dispatch('setClassifiedOfferState', {
        offerId,
        state,
        successCallback
      })
      loading.value = false
    }

    const onCounteroffer = (offer: ClassifiedOfferEntry) => {
      offerToCounter.value = offer
      showMakeOfferModal.value = true
    }

    const onMakeOfferClose = () => {
      showMakeOfferModal.value = false
      offerToCounter.value = null
    }

    const updateOffer = (updatedOffer: ClassifiedOfferEntry) => {
      if (thread.value) {
        thread.value.offer = updatedOffer
      }

      commit(UPDATE_CLASSIFIED_OFFER, updatedOffer)
    }

    return {
      modalToggleFunc,
      loading,
      hideModal,
      fetchOffersForClassified,
      currentPage,
      numberOfPages,
      pagination,
      handlePageChange,
      classifiedOffers,
      offerCount,
      swipeDirection,
      selectedOffer,
      onOfferClick,
      onDeleteOffer,
      transitioning,
      ciAngleLeftLight,
      goBack,
      mainHash,
      thread,
      updateThread,
      fetchThread,
      threadRead,
      onNewMessages,
      deleteThread,
      hasOfferForClassified,
      setOfferState,
      onCounteroffer,
      updateOffer,
      showMakeOfferModal,
      offerToCounter,
      onMakeOfferClose,
      ownedDealers,
      faTimes,
      ciOffers
    }
  }
})
